/* Restore the two product discovery sections independently of custom.js. */
const historyKey = 'theme4:recently:id';

function readHistory() {
  try {
    return [...new Set((localStorage.getItem(historyKey) || '').split(','))]
      .filter((id) => /^\d+$/.test(id))
      .slice(0, 10);
  } catch {
    return [];
  }
}

function recordVisit(id, history) {
  if (!/^\d+$/.test(id)) return;
  try {
    localStorage.setItem(historyKey, [id, ...history.filter((item) => item !== id)].slice(0, 10).join(','));
  } catch {
    // Browsing remains usable when storage is unavailable.
  }
}

class ProductDiscovery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.section = this.closest('.shopify-section');
    this.requestUrl = new URL(this.dataset.url, window.location.origin);

    if (this.localName === 'product-recently') {
      const currentId = this.dataset.id || '';
      const history = readHistory();
      // Record on arrival, even if the visitor never scrolls to this section.
      recordVisit(currentId, history);
      const previousIds = history.filter((id) => id !== currentId);
      if (this.dataset.reverse === 'true') previousIds.reverse();
      if (!previousIds.length) {
        this.setEmpty();
        return;
      }
      const exclusion = /^\d+$/.test(currentId) ? ` NOT id:${currentId}` : '';
      this.requestUrl.searchParams.set('q', previousIds.map((id) => `id:${id}`).join(' OR ') + exclusion);
    } else {
      // Shopify's recommendation endpoint supports at most ten products.
      const limit = Number(this.requestUrl.searchParams.get('limit')) || 10;
      this.requestUrl.searchParams.set('limit', String(Math.max(1, Math.min(10, limit))));
    }

    if (this.querySelector('hdt-card-product')) {
      this.dataset.discoveryState = 'loaded';
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadProducts();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.observer.observe(this.section || this);
    } else {
      this.loadProducts();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.initialized = false;
  }

  setEmpty() {
    this.dataset.discoveryState = 'empty';
    if (this.section) this.section.hidden = true;
  }

  async loadProducts() {
    if (this.loading) return;
    this.loading = true;
    this.dataset.discoveryState = 'loading';
    try {
      const response = await fetch(this.requestUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector(this.localName);
      if (!this.isConnected) return;
      if (!content?.querySelector('hdt-card-product')) {
        this.setEmpty();
        return;
      }
      if (this.section) this.section.hidden = false;
      this.innerHTML = content.innerHTML;
      this.dataset.discoveryState = 'loaded';
      document.dispatchEvent(new CustomEvent('currency:update'));
    } catch (error) {
      this.dataset.discoveryState = 'error';
      console.warn(`[AURLONA ${this.localName}] Unable to load products:`, error);
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', class extends ProductDiscovery {});
}
if (!customElements.get('product-recently')) {
  customElements.define('product-recently', class extends ProductDiscovery {});
}
